import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import { logError } from "../../../utils/appUtils";
import {
  FilePaths,
  InboundStatus,
  NoteType,
  VALIDATION_MSG,
} from "../../../utils/constants";
import { saveRemoveFiles } from "../../../utils/fileUtils";
import { areTimeslotsEqual, validateAppointment } from "../../../utils/dateUtils";
import { deepRedirect, normalizeValue } from "../../../utils/utils";

// Customer resubmit of a rejected inbound advice. Per product decision, a
// resubmit goes straight back to manager review (ADVICE_REVIEW) — it does NOT
// re-run the /parseInboundAdvice AI pipeline. Routes through the lambda writer
// (data.__saveContext) so the picked timeslot is booked/updated/deleted
// alongside the parent update; the dataProvider sanitizes on return.
export const DialogSaveButton = ({
  record,
  backPath,
  selectedTimeslot,
  isSaving,
  setIsSaving,
  ...props
}) => {
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const { pristine, submitting } = useFormState({
    subscription: { pristine: true, submitting: true },
  });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  const formValidate = () => {
    const newFormState = form.getState();
    if (newFormState.invalid) notify(VALIDATION_MSG, "warning");
  };

  const handleValidateSave = async () => {
    let tempMsg = null;

    // Re-check availability only when the customer picked a different slot.
    if (selectedTimeslot && !areTimeslotsEqual(record.timeslot, selectedTimeslot)) {
      const error = await validateAppointment(
        selectedTimeslot,
        record.timeslot?.id,
      );
      if (error) {
        tempMsg = `${selectedTimeslot.time} is no longer available. Please select a new time.`;
      }
    }

    if (tempMsg) {
      setMessage(tempMsg);
      setOpen(true);
    } else {
      handleSave();
    }
  };

  const handleDialogClose = () => setOpen(false);

  const handleSave = () => {
    formValidate();
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };

    data.status = InboundStatus.ADVICE_REVIEW;

    data.poNumber = data.poNumber ? data.poNumber : "";
    data.normalizedPoNumber = normalizeValue(data.poNumber);
    data.railCarNumber = data.railCarNumber || "";
    data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);

    data.customFields = getCustomFieldsToSave(
      data.customFields,
      record.account?.inboundCustomFields,
    );

    data.history = [
      ...(data.history || []),
      {
        text: `Advice resubmitted | status: ${data.status}`,
        by: window.appUser?.fullName,
        date: new Date(),
        type: NoteType.SHIPMENT,
      },
    ];

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    try {
      await saveRemoveFiles(
        record,
        data,
        "attachments",
        `${FilePaths.INBOUND}/${Date.now()}`,
      );
    } catch (err) {
      logError("inbounds:DialogSaveButton:transform", err, notify, redirect);
      setIsSaving(false);
      return Promise.reject();
    }

    // Attach the picked timeslot (fall back to the existing booking when the
    // customer didn't touch the scheduler) and route through the lambda writer.
    // The dataProvider's __saveContext branch calls lambdaSaveInbound, which
    // books/updates/deletes the Timeslot appointment and does the full parent
    // update; no sanitize here — the dataProvider sanitizes on return.
    data.timeslot =
      selectedTimeslot === undefined ? record.timeslot : selectedTimeslot;
    delete data.customFieldSelect;
    // AppSync auto-stamps updatedAt; the direct lambda write does not.
    data.updatedAt = new Date().toISOString();
    data.__saveContext = { isRollback: false };
    return data;
  };

  const onSuccess = () => {
    notify("Inbound advice resubmitted successfully", "success");
    deepRedirect(history, backPath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("inbounds:DialogSaveButton:onFailure", error, notify);
  };

  return (
    <>
      <SaveButton
        {...props}
        label="Resubmit"
        submitOnEnter={false}
        undoable={false}
        disabled={pristine || isSaving}
        saving={isSaving || submitting}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={message}
        cancel="Ok"
        onClose={handleDialogClose}
      />
    </>
  );
};
